import React, { ReactElement, ReactNode, RefObject, useRef } from 'react';
import { TooltipPosition, useTooltipPosition } from './useTooltipPosition';
import ImageComponent from '@spa/components/image-component/image.component';
import { ImageProps } from 'next/image';
import TooltipComponent from '../tooltip/tooltip.component';
import classNames from 'clsx';
import styles from './tooltip-fixed-trigger.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';

export type TooltipImage = Omit<ImageProps, 'alt' | 'title'>;

interface Props {
  readonly alt?: string;
  readonly children: ReactNode;
  readonly fitContent?: boolean;
  readonly hideMobile?: boolean;
  readonly image?: TooltipImage;
  readonly isColorTile?: boolean;
  readonly unoptimized?: boolean;
}

const TooltipFixedTriggerComponent = ({
  alt,
  children,
  fitContent,
  hideMobile,
  image,
  isColorTile = false,
  unoptimized = true
}: Props): ReactElement => {
  const buttonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
  const position: TooltipPosition = useTooltipPosition(buttonRef);
  const { formatMessage } = useNamespacedIntl('tooltip');
  const fallbackImage: TooltipImage = {
    src: '/assets/icons/info.svg',
    height: 20,
    width: 20
  };
  const fallbackAlt: string = formatMessage('infoAlt');

  const textClassName: string = classNames(styles['tooltip-fixed-trigger__tooltip'], 'text-xs', {
    [styles['tooltip-fixed-trigger__tooltip--top']]: position === TooltipPosition.TOP,
    [styles['tooltip-fixed-trigger__tooltip--left']]: position === TooltipPosition.LEFT,
    [styles['tooltip-fixed-trigger__tooltip--right']]: position === TooltipPosition.RIGHT
  });

  return (
    <button
      ref={buttonRef}
      className={classNames(styles['tooltip-fixed-trigger'], {
        [styles['tooltip-fixed-trigger--color-tile']]: isColorTile,
        [styles['tooltip-fixed-trigger--hide-mobile']]: hideMobile
      })}
    >
      <ImageComponent
        className={styles['tooltip-fixed-trigger__image']}
        {...(image ?? fallbackImage)}
        alt={alt ?? fallbackAlt}
        unoptimized={unoptimized}
      />
      <TooltipComponent className={textClassName} fitContent={fitContent} position={position}>
        {children}
      </TooltipComponent>
    </button>
  );
};

export default TooltipFixedTriggerComponent;
